import _defineProperty from "@babel/runtime/helpers/defineProperty";
/*
 *                     NOTICE
 *            COPYRIGHT 2025 Ab Initio
 *       UNPUBLISHED - ALL RIGHTS RESERVED
 *
 * This program is confidential and a trade secret of Ab Initio.
 * The receipt or possession of this program does not convey any
 * rights to reproduce or disclose its contents, or to manufacture,
 * use or sell anything that it may describe, in whole or in part,
 * without the specific written consent of Ab Initio.
 */

import * as AbReactResources from "abreact/generated/locale/AbReactResources";
import { AbstractRouteHandler } from "abreact/incubator/ui/app/router/simple/AbstractRouteHandler";
import { NavigationService } from "abreact/incubator/ui/app/NavigationService";
import { RouteChangeType } from "abreact/incubator/ui/app/router/RouteChangeContext";
import { ReactRouteImpl } from "abreact/incubator/ui/app/router/ReactRouteImpl";
import { RouteServicesRenderer } from "abreact/incubator/ui/app/router/simple/RouteServicesRenderer";
import { AbWebServices } from "abweb/service/AbWebServices";
import { ArrayUtils } from "abweb/utils/ArrayUtils";
import { DebugUtils } from "abweb/utils/DebugUtils";
import { LogUtils } from "abweb/utils/log/LogUtils";
const LOG = LogUtils.getLogger(__filename);
export class AbSDKRouteHandler extends AbstractRouteHandler {
  constructor(routeSpecHelper) {
    super(routeSpecHelper.registeredRoutes);
    _defineProperty(this, "instanceId", void 0);
    _defineProperty(this, "routeSpec", void 0);
    _defineProperty(this, "routeViewer", void 0);
    _defineProperty(this, "mountMajorRouteState", void 0);
    // Component state related callbacks
    // The reason need a routeTitle callback is minor changes initiated by browser back button.
    // The route updates correctly (based on routeProps), but need to update route title (from already mounted view) as well
    _defineProperty(this, "dynamicRouteTitleCallback", void 0);
    _defineProperty(this, "hasUnsavedChangesCallback", void 0);
    // Beta callbacks (only used by EI)
    _defineProperty(this, "routeChangeTypeCallback", void 0);
    _defineProperty(this, "continueRouteChangeCallback", void 0);
    _defineProperty(this, "onUnmountMajorRouteCallback", void 0);
    _defineProperty(this, "retryRouteChangeCallback", void 0);
    _defineProperty(this, "hocParamPostProcessors", []);
    _defineProperty(this, "routeServicesImpl", void 0);
    _defineProperty(this, "routeHocServicesImpl", void 0);
    this.routeSpecHelper = routeSpecHelper;
    this.instanceId = AbSDKRouteHandler.nextInstanceId++;
    this.routeSpec = this.routeSpecHelper.routeSpec;
    this.routeServicesImpl = {
      setCallbacks: this.setCallbacks.bind(this),
      updateRoute: this.updateRoute.bind(this)
    };
    this.routeHocServicesImpl = {
      setCallbacks: this.setHocCallbacks.bind(this),
      getOriginalRoute: this.getOriginalRoute.bind(this)
      // No: do not support ReactRouteRenderContext prop, as want useContext("ReactRouteRenderContext") to be sole accessor
      // getRouteRenderContext: this.getRouteRenderContext.bind(this),
    };
    this.routeViewer = new RouteServicesRenderer(this.routeServicesImpl, this.routeHocServicesImpl, routeSpecHelper.viewImportSpec, routeSpecHelper.routeSpec.view.hocFnImportSpecs);
  }
  getCurrentRouteTitle() {
    if (this.dynamicRouteTitleCallback) {
      return this.dynamicRouteTitleCallback();
    }
    const specRouteTitle = this.routeSpec.routeTitle;
    if (specRouteTitle == null) {
      return AbReactResources.message_loading(); // ABSDK-358
    } else {
      return typeof specRouteTitle === "string" ? specRouteTitle : specRouteTitle(this.getCurrentRouteProps());
    }
  }
  getCurrentRoute() {
    const currentRouteProps = this.getCurrentRouteProps();
    // return new params, extraParams objects
    // let reactRoute: ReactRoute<RoutePropsT> = this.routeSpecHelper.buildRouteFromProps(currentRouteProps);
    let routeParams = this.routeSpec.routeParamHandler?.paramsFromProps(currentRouteProps) ?? {
      routeParams: {},
      routeExtraParams: {}
    };
    if (this.hocParamPostProcessors.length) {
      for (const paramProcessor of this.hocParamPostProcessors) {
        routeParams = paramProcessor(routeParams);
      }
    }
    // ReactRouteImpl creates COPY of props and params args, so returned props and params are modifiable (ABSDK-4999)
    const reactRoute = new ReactRouteImpl(this.routeSpec.routeTemplate, currentRouteProps, routeParams.routeParams, routeParams.routeExtraParams);
    return reactRoute;
  }
  getRouteChangeType(newRoute) {
    if (!this.routeChangeTypeCallback) {
      // Default implementation returns MAJOR | MINOR only (MID not supported) based on
      // below isMajorRouteChange method (uses routespec.isMajorPropsEqual function)
      return super.getRouteChangeType(newRoute); // Invokes below isMajorRouteChange method
    }
    const newRegRoute = NavigationService.getSingleton().getRegisteredRoute(newRoute.routeTemplate);
    if (!newRegRoute) {
      // Change from absdk route (this route) to a new non-absdk route is always a major route change!
      // Also do not have easy way to build newProps for non-absdk routes
      return RouteChangeType.MAJOR;
    }

    // Do NOT support mid-route changes unless both current and new are absdk routes
    const routeChangeType = this.routeChangeTypeCallback(newRegRoute, newRoute.routeProps);
    // Defensive check to ensure mid route changes are valid
    if (routeChangeType === RouteChangeType.MID && !this.isValidMidRouteChange(newRegRoute)) {
      // If invalid mid route change, then this method returns false in production; in dev it throws!
      // So in production an invalid mid route change will conservatively end up as a major route change.
      return RouteChangeType.MAJOR;
    }
    // If invalid mid route change, then this method returns false in production; in dev it throws!
    // So in production an invalid mid route change will conservatively end up as a major route change.
    return routeChangeType;
  }
  isValidMidRouteChange(newRegRoute) {
    // For now, just verify that current route (this route) and new route both have same component stack, including HOCs,
    // as that is required for 'mid' route changes to work.  For now, just verify that the routeSpec component and HOC
    // 'exportName' values are the same.  Ideally: check for same loaded component/hoc instances, but don't want
    // to deal with lazy loading right now and probably exportNames are close enough.
    const currComponentExportNames = this.routeSpecHelper.getViewExportNames();
    const newComponentExportNames = this.routeSpecHelper.getViewExportNames(newRegRoute.routeSpec);
    if (!ArrayUtils.isEquals(currComponentExportNames, newComponentExportNames)) {
      // This exception is for th developer, so give enough detail to diagnose the issue
      const errMsg = "Can not have a mid-route change between the two below routes as they have different react component stacks." + `\n  Current route '${this.routeSpec.routeTemplate}', component stack exportNames: [${currComponentExportNames.join(",")}]` + `\n  New route '${newRegRoute.routeSpec.routeTemplate}', component stack exportNames: [${newComponentExportNames.join(",")}]`;
      DebugUtils.developerError(errMsg); // during dev it throws, in production just logs
      return false;
    }
    return true;
  }

  // Override to leverage routeSpec.isMajorPropsEqual callback.  Used when routeChangeTypeCallback not defined.
  isMajorRouteChange(newRoute) {
    if (!this.routeSpecHelper.isRouteRegistered(newRoute.routeTemplate)) {
      // For the AbSDK View strategy, both current and new route have to be backed by the same route (aka route template)
      // in order to support minor route changes
      return true;
    }
    const majorPropsEqual = this.routeSpec.routeParamHandler?.isMajorPropsEqual ? this.routeSpec.routeParamHandler.isMajorPropsEqual(this.getCurrentRouteProps(), newRoute.routeProps) : true; // if isMajorParamsEqual not defined, assume no major params, so they are 'equal'

    return !majorPropsEqual; // it is a major route change if the major params are not equal
  }

  // Do not make public, instead external callers can get props via: getCurrentRoute().routeProps
  /** @returns COPY of current route props */
  getCurrentRouteProps() {
    return this.routeViewer.getRawRouteProps();
  }

  /** @inheritDoc */
  isAuthorized(newRouteProps) {
    return this.routeSpec.isAuthorized?.(newRouteProps) ?? true;
  }
  continueRouteChange(rcc) {
    if (!this.continueRouteChangeCallback || AbstractRouteHandler.vetoDialogBeingDisplayed) {
      // Default implementation returns MAJOR | MINOR only (MID not supported) based on
      // below isMajorRouteChange method (uses routespec.isMajorPropsEqual function)
      // Also returns false if already in veto dialog workflow (AbstractRouteHandler.vetoDialogBeingDisplayed)
      return super.continueRouteChange(rcc);
    }
    const continueRouteChange = this.continueRouteChangeCallback(rcc.routeChangeType, rcc.newRegisteredRoute, rcc.newReactRoute.routeProps);
    return continueRouteChange;
  }
  async mountMajorRoute(routeChangeContext) {
    const continueToView = await this.mountRoute(routeChangeContext);
    if (continueToView) {
      // Only allow main routes to set/unset the context-sensitive help menu item.
      // Per discussion with Andy A., secondary routes are unlikely to have contextHelp, AND our current
      // Help service supports at most one context help menu item.  If we have the need in the future for secondary
      // routes to also support context help, will need to redesign approach (probably including a UX discussion)
      if (routeChangeContext.routeRenderContext === NavigationService.getSingleton().mainAreaRouteRenderContext) {
        // on major route changes in mainArea, reset banner's context help menu item (see AbHelpButton)
        AbWebServices.helpService.contextHelp = this.routeSpec.contextHelp;
      }
    }
    return continueToView;
  }
  onMinorRouteChange(routeChangeContext) {
    return this.mountRoute(routeChangeContext);
  }
  async unmountMajorRoute(routeChangeType = RouteChangeType.MAJOR) {
    if (routeChangeType === RouteChangeType.MAJOR) {
      if (this.onUnmountMajorRouteCallback) {
        // wait for async unmounting to finish (e.g., mt cleanup), which for EI needs to finish before new route can be mounted
        await this.onUnmountMajorRouteCallback();
      }
      return super.unmountMajorRoute(routeChangeType);
    }
    // Do NOTHING on unmount for MID route changes as destroyListeners/popups will be transferred to new RH instance
    // via updateFromOldRouteHandler method below
  }
  mountRoute(routeChangeContext) {
    if (routeChangeContext.newReactRoute) {
      this.mountMajorRouteState = routeChangeContext.newReactRoute;
      this.routeViewer.setRawRouteProps(routeChangeContext.newReactRoute.routeProps, true);
    }
    return Promise.resolve(true);
  }
  renderView() {
    return this.routeViewer.renderView();
  }
  hasUnsavedChanges() {
    return this.hasUnsavedChangesCallback ? this.hasUnsavedChangesCallback() : false;
  }
  displayVetoRouteChangeDlg() {
    return this.retryRouteChangeCallback ? this.retryRouteChangeCallback() : super.displayVetoRouteChangeDlg();
  }

  ///////  RouteServices Implementation: supports routing functionality that is (internal) component state related  ///

  setCallbacks(callbacks) {
    // Only clobber an existing member variable if the callback prop is explicitly defined - ABSDK-4576
    // The prop value can be undefined to 'unset' the callback.

    if (callbacks.hasOwnProperty("hasUnsavedChangesCallback")) this.hasUnsavedChangesCallback = callbacks.hasUnsavedChangesCallback;
    if (callbacks.hasOwnProperty("retryRouteChangeCallback")) this.retryRouteChangeCallback = callbacks.retryRouteChangeCallback;
    if (callbacks.hasOwnProperty("routeChangeTypeCallback")) {
      if (callbacks.routeChangeTypeCallback && this.routeSpec.routeParamHandler?.isMajorPropsEqual) {
        // Do NOT to handle multiple sources of determining if a route change is major or minor, so proactively throw.
        throw new Error(`Route ${this.routeSpec.routeTemplate} can not both define the routeSpec's 'isMajorPropsEqual' hook` + " AND use the routeService's routeChangeTypeCallback." + "\n Typically fixed by removing the isMajorPropsEqual definition in favor of using the more general routeChangeTypeCallback method.");
      }
      this.routeChangeTypeCallback = callbacks.routeChangeTypeCallback;
    }
    if (callbacks.hasOwnProperty("continueRouteChangeCallback")) this.continueRouteChangeCallback = callbacks.continueRouteChangeCallback;
    if (callbacks.hasOwnProperty("onUnmountMajorRouteCallback")) this.onUnmountMajorRouteCallback = callbacks.onUnmountMajorRouteCallback;

    // Do last as has updateRoute ramifications
    if (callbacks.hasOwnProperty("dynamicRouteTitleCallback") && this.dynamicRouteTitleCallback !== callbacks.dynamicRouteTitleCallback) {
      this.dynamicRouteTitleCallback = callbacks.dynamicRouteTitleCallback;
      // The 'temporary' route title is currently being displayed (calculated from spec's routeTitle value),
      // so update it with new calculated title and take care NOT to bookmark the current 'temporary' title.
      this.updateRoute(undefined, false);
    }
  }

  /** Invoked by view for view-initiated minor route changes */
  updateRoute(newRouteProps, bookmarkCurrentUrl = true, forceUpdate = false) {
    // ensures getCurrentRoute is up to date based on newRouteProps (invoked by below updateCurrentViewRoute)
    if (newRouteProps || forceUpdate) {
      this.routeViewer.setRawRouteProps(newRouteProps ?? this.getCurrentRouteProps(), forceUpdate);
    }

    // updateCurrentViewRoute invokes above getCurrentRoute/getCurrentRouteTitle implementations
    NavigationService.getSingleton().updateCurrentViewRoute(this, bookmarkCurrentUrl);
  }
  updateFromOldRouteHandler(oldRH) {
    // super copies current popup/pfs/destroyListener state, as 'registered' by the current view component and
    // don't want to have to re-register.  Keeps any popups open during MID change, without leaking - see ABSDK-4878
    super.updateFromOldRouteHandler(oldRH);

    // copy over current view component's callbacks
    this.updateAbSDKCallbacks(oldRH);
  }

  /**
   * On mid-level route changes (BRE-only), the view component has already registered the view callbacks with the
   * current route handler, and they need to be transferred to the next route handler.
   * @param oldRH
   */
  updateAbSDKCallbacks(oldRH) {
    // routeServices callbacks
    this.dynamicRouteTitleCallback = oldRH.dynamicRouteTitleCallback;
    this.hasUnsavedChangesCallback = oldRH.hasUnsavedChangesCallback;
    this.retryRouteChangeCallback = oldRH.retryRouteChangeCallback;
    // The Hoc callbacks are also set, as under the assumption that on mid-route changes, the HOCs
    // between the two routes are same (as is the view component).  As such, the HOC(s) do not unmount
    // (just receive new props).  ls a result, also need to carry over the previously registered HOC component callbacks.
    this.hocParamPostProcessors = [...oldRH.hocParamPostProcessors];

    // RouteServicesBeta callbacks
    this.routeChangeTypeCallback = oldRH.routeChangeTypeCallback;
    this.continueRouteChangeCallback = oldRH.continueRouteChangeCallback;
    this.onUnmountMajorRouteCallback = oldRH.onUnmountMajorRouteCallback;
  }

  ////// RouteHocServices implementation  //////////

  getOriginalRoute() {
    return this.mountMajorRouteState;
  }
  setHocCallbacks(callbacks) {
    // Currently no way to unregister a paramsPostProcessor callback
    // (but no known use case for unregistering a paramsPostProcessor either)
    if (callbacks.paramsPostProcessor) {
      this.hocParamPostProcessors.push(callbacks.paramsPostProcessor);
      // An hoc might need to add a new (extra) param to the browser url.
      this.updateRoute(undefined, false);
    }
  }
}
_defineProperty(AbSDKRouteHandler, "nextInstanceId", 0);
//# sourceMappingURL=AbSDKRouteHandler.js.map
